// App.tsx - メインアプリケーションコンポーネント

import { Alert, Box, Button, CircularProgress, Container, CssBaseline, Snackbar, ThemeProvider, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { jaJP } from '@mui/material/locale';
import React, { ReactNode, useEffect, useState } from 'react';

// コンポーネント
import Dashboard from './components/dashboard/Dashboard';
import FileUpload from './components/FileUpload';
import FileList from './components/FileList';
import Header from './components/Header';
import Login from './components/Login';
import RequestPasswordReset from './components/RequestPasswordReset';
import ResetPassword from './components/ResetPassword';
import Register from './components/Register';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import TrialRequest from './pages/TrialRequest';
import FirstLoginPassword from './pages/FirstLoginPassword';
import TrialProfileSetup from './pages/TrialProfileSetup';
import RequireAuth from './components/RequireAuth';
import VerificationCode from './components/VerificationCode';
import ViewerActivation from './components/ViewerActivation';
import TooManyRequests from './components/TooManyRequests';
import DbAdmin from './components/DbAdmin';
import DbAdminView from './components/DbAdminView';
import LoginHistoryView from './components/LoginHistoryView';
import AnalyticsLogView from './components/AnalyticsLogView';
import NoticeList from './components/NoticeList';
import Footer from './components/Footer';
import UserSettings from './components/UserSettings';
import AccountDeletionComplete from './components/AccountDeletionComplete';
import AnalyticsPageTracker from './components/AnalyticsPageTracker';

// 認証コンテキスト
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NoticeProvider } from './contexts/NoticeContext';
import { subscribeRateLimit } from './utils/rateLimitEvents';

// テーマ設定（日本語対応）
const theme = createTheme(
  {
    palette: {
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
    typography: {
      fontFamily: [
        '"Noto Sans JP"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
    },
  },
  jaJP // 日本語ローカライゼーション
);

// React Query クライアント
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type AppShellProps = {
  children: ReactNode;
};

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const location = useLocation();
  const isNoticeManagement = location.pathname.startsWith('/notice_management');
  const isDbAdminPage =
    location.pathname.startsWith('/db-admin') ||
    location.pathname.startsWith('/db-view') ||
    location.pathname.startsWith('/login-history') ||
    location.pathname.startsWith('/analytics-log');

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <Header />
      <Box component="main" sx={{ flexGrow: 1, width: '100%' }}>
        <Container maxWidth={isDbAdminPage ? 'xl' : 'lg'} sx={{ mt: 4, mb: 4 }}>
          {children}
        </Container>
      </Box>
      {!isNoticeManagement && <Footer />}
    </Box>
  );
};

const HomeRoute: React.FC = () => {
  const { user } = useAuth();
  const isReadOnlyUser = Boolean(user?.isViewer || user?.isExpired);

  return (
    <>
      {!isReadOnlyUser && <FileUpload />}
      <FileList />
    </>
  );
};

const HeartbeatConnectionSnackbar: React.FC = () => {
  const { heartbeatConnectionUnstable } = useAuth();

  return (
    <Snackbar
      open={heartbeatConnectionUnstable}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={null}
    >
      <Alert severity="warning" sx={{ width: '100%' }}>
        サーバーとの接続が不安定です。復旧次第、自動で再接続します。
      </Alert>
    </Snackbar>
  );
};

const RateLimitSnackbar: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  useEffect(() => subscribeRateLimit(() => setOpen(true)), []);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const handleRetry = () => {
    setOpen(false);
    void queryClient.refetchQueries({ type: 'active' });
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={null}
      onClose={handleClose}
    >
      <Alert
        severity="warning"
        sx={{ width: '100%' }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleRetry}>
              再試行
            </Button>
            <Button color="inherit" size="small" onClick={() => handleClose()}>
              閉じる
            </Button>
          </>
        }
      >
        リクエストが集中しています。少し時間をおいてから再試行してください。
      </Alert>
    </Snackbar>
  );
};

const SubscriptionAccessRoute: React.FC<AppShellProps> = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (user?.isViewer) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (user?.hasActiveSubscription && user?.needsProfileCompletion) {
    return <Navigate to="/trial-profile-setup" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NoticeProvider>
          <ThemeProvider theme={theme}>
            <Router>
              <CssBaseline />
              <HeartbeatConnectionSnackbar />
              <RateLimitSnackbar />
              <AnalyticsPageTracker />
              <AppShell>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <HomeRoute />
                      </RequireAuth>
                    }
                  />
                  <Route path="/dashboard/:fileId" element={<RequireAuth><Dashboard /></RequireAuth>} />
                  <Route path="/notice" element={<NoticeList />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/request-password-reset" element={<RequestPasswordReset />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/trial-request" element={<TrialRequest />} />
                  <Route
                    path="/subscription"
                    element={
                      <SubscriptionAccessRoute>
                        <Subscription />
                      </SubscriptionAccessRoute>
                    }
                  />
                  <Route
                    path="/subscription/success"
                    element={
                      <RequireAuth requireSubscription={false} disallowViewer>
                        <SubscriptionSuccess />
                      </RequireAuth>
                    }
                  />
                  <Route path="/verify" element={<VerificationCode />} />
                  <Route path="/viewer-activation" element={<ViewerActivation />} />
                  <Route
                    path="/first-login-password"
                    element={
                      <RequireAuth requireSubscription={false}>
                        <FirstLoginPassword />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/trial-profile-setup"
                    element={
                      <RequireAuth requireSubscription={false} disallowViewer>
                        <TrialProfileSetup />
                      </RequireAuth>
                    }
                  />
                  <Route path="/db-admin" element={<RequireAuth><DbAdmin /></RequireAuth>} />
                  <Route path="/db-view" element={<RequireAuth><DbAdminView /></RequireAuth>} />
                  <Route path="/login-history" element={<RequireAuth><LoginHistoryView /></RequireAuth>} />
                  <Route path="/analytics-log" element={<RequireAuth><AnalyticsLogView /></RequireAuth>} />
                  <Route
                    path="/user-settings"
                    element={
                      <RequireAuth requireSubscription={false} disallowViewer>
                        <UserSettings />
                      </RequireAuth>
                    }
                  />
                  <Route path="/account-deleted" element={<AccountDeletionComplete />} />
                  <Route path="/429" element={<TooManyRequests />} />
                </Routes>
              </AppShell>
            </Router>
          </ThemeProvider>
        </NoticeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
